import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';

export interface LicitacaoFeed {
  id: number;
  portal: string;
  numero_edital: string | null;
  objeto: string;
  orgao: string | null;
  uf: string | null;
  municipio: string | null;
  valor_estimado: number | null;
  prazo_abertura: string | null;
  link: string | null;
  modalidade: string | null;
  /** Identificador oficial da contratação no PNCP (ENR/MOD.1). */
  numero_controle_pncp: string | null;
  /** Esfera do órgão: federal | estadual | municipal (mig. 060). */
  esfera: string | null;
  /** CNPJ do órgão comprador, 14 dígitos (mig. 060). */
  orgao_cnpj: string | null;
  /** Situação da contratação no PNCP (ex.: "Divulgada no PNCP") (mig. 032). */
  situacao_pncp: string | null;
  scraped_at: string | null;
  created_at: string;
  resumo_ia: string | null;
  categorias_ia: string[] | null;
  palavras_chave_ia: string[] | null;
  relevancia_score: number | null;
  ia_processado: boolean;
  ia_fonte: string | null;
  status_calculado: 'ativa' | 'vencida';
  campos_ausentes: string[];
  aderencias?: LicitacaoAderencia[];
  /** Iminência de deserto (ENR.2) — estimativa, pode ser null se sem base. */
  risco_deserto_score: number | null;
  risco_deserto_faixa: 'baixo' | 'medio' | 'alto' | null;
  deserta_confirmada: boolean;
  /** Nota CAPAG do município (ENR.1) — null se sem dado do Tesouro. */
  capag_nota: 'A+' | 'A' | 'B+' | 'B' | 'C' | 'D' | null;
  /** Nº de fornecedores distintos observados na sessão via Radar/PCP (ENR.3/7) — null/0 se sem dado. */
  participantes_contagem?: number | null;
}

export interface LicitacaoAderencia {
  empresa_id: number;
  empresa_nome: string;
  aderencia_score: number | null;
  recomendacao: 'participar' | 'declinar' | 'analise_manual' | null;
}

interface LicitacoesFeedResponse {
  success: boolean;
  data: {
    licitacoes: LicitacaoFeed[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
  };
}

export interface LicitacoesFilters {
  search?: string;
  /** UF única (busca legada). Combina com `ufs_multi`/`regioes` via AND no backend. */
  uf?: string;
  /** Lista de UFs (OR entre elas). Modelo usado pelo painel de filtros. */
  ufs_multi?: string[];
  /** Regiões (`norte`, `nordeste`, `centro_oeste`, `sudeste`, `sul`). */
  regioes?: string[];
  municipio?: string;
  /** Modalidade por busca parcial (ILIKE). Não envie junto com `modalidades`. */
  modalidade?: string;
  /** Modalidades por igualdade exata — valores vindos de `/licitacoes/meta`. */
  modalidades?: string[];
  /** Esfera administrativa: `federal` | `estadual` | `municipal`. */
  esferas?: string[];
  /** CNPJ do órgão, somente dígitos (14). */
  orgao_cnpj?: string;
  orgao_nome?: string;
  numero_edital?: string;
  portal?: string[];
  objeto_inclui?: string[];
  objeto_exclui?: string[];
  pago?: boolean;
  empresa_ids?: number[];
  valor_min?: number;
  valor_max?: number;
  data_de?: string;
  data_ate?: string;
  categoria?: string;
  relevancia_min?: number;
  /** Faixa(s) de risco de deserto: `baixo` | `medio` | `alto` (ENR.2). */
  risco_deserto?: string[];
  /** Nota CAPAG mínima: `A+` | `A` | `B+` | `B` | `C` | `D` (ENR.1). */
  capag_min?: string;
  somente_ativas?: boolean;
  somente_ia?: boolean;
  sort?: 'prazo_abertura' | 'valor_estimado' | 'relevancia_score' | 'scraped_at';
  order?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface LicitacaoFonte {
  id: number;
  nome: string;
  dominio: string | null;
  portal_label: string;
  ufs: string[] | null;
  acesso_pago: boolean;
  ativo: boolean;
}

interface LicitacoesFontesResponse {
  success: boolean;
  data: { fontes: LicitacaoFonte[] };
}

export interface GastoPorUf {
  uf: string;
  total_valor_estimado: number;
  total_licitacoes: number;
}

export interface GastoPorMes {
  mes: string;
  total_valor_estimado: number;
  total_licitacoes: number;
}

export interface GastoPorOrgao {
  orgao: string;
  total_valor_estimado: number;
  total_licitacoes: number;
}

export interface GastoPorNicho {
  total_valor_estimado: number;
  total_licitacoes: number;
  por_uf: GastoPorUf[];
  por_mes: GastoPorMes[];
  top_orgaos: GastoPorOrgao[];
}

interface GastoPorNichoResponse {
  success: boolean;
  data: GastoPorNicho;
}

export type GastoPorNichoFilters = Pick<
  LicitacoesFilters,
  'search' | 'uf' | 'ufs_multi' | 'regioes' | 'municipio' | 'modalidade' | 'modalidades' |
  'esferas' | 'orgao_cnpj' | 'orgao_nome' | 'numero_edital' |
  'portal' | 'objeto_inclui' | 'objeto_exclui' |
  'pago' | 'valor_min' | 'valor_max' | 'data_de' | 'data_ate' | 'categoria' | 'relevancia_min' |
  'somente_ativas'
>;

/**
 * Conjunto completo de filtros persistível (ex.: em `buscas_salvas.filtros`).
 * Exclui apenas o estado de paginação, que é efêmero.
 */
export type LicitacoesFiltrosPersistiveis = Omit<LicitacoesFilters, 'page' | 'limit'>;

interface AtualizarLicitacoesResponse {
  success: boolean;
  data: {
    message: string;
    crawler: {
      status: string;
      stats?: {
        fetched: number;
        new: number;
        updated: number;
        failed: number;
      };
      duration?: string;
    };
  };
}

export function useLicitacoesFeed(filters?: LicitacoesFilters) {
  return useQuery({
    queryKey: ['licitacoes-feed', filters],
    queryFn: async () => {
      const params: Record<string, string | number | boolean> = {
        page: filters?.page || 1,
        limit: filters?.limit || 20,
        somente_ativas: filters?.somente_ativas !== false ? 'true' : 'false'
      };

      if (filters?.search) params.search = filters.search;
      if (filters?.uf) params.uf = filters.uf;
      if (filters?.ufs_multi && filters.ufs_multi.length > 0) params.ufs_multi = filters.ufs_multi.join(',');
      if (filters?.regioes && filters.regioes.length > 0) params.regioes = filters.regioes.join(',');
      if (filters?.municipio) params.municipio = filters.municipio;
      // `modalidades` (igualdade exata) tem precedência sobre `modalidade`
      // (ILIKE parcial): o backend combina os dois com AND, então os dois
      // juntos na mesma requisição zerariam o resultado.
      if (filters?.modalidades && filters.modalidades.length > 0) {
        params.modalidades = filters.modalidades.join(',');
      } else if (filters?.modalidade) {
        params.modalidade = filters.modalidade;
      }
      if (filters?.esferas && filters.esferas.length > 0) params.esferas = filters.esferas.join(',');
      if (filters?.orgao_cnpj) params.orgao_cnpj = filters.orgao_cnpj;
      if (filters?.orgao_nome) params.orgao_nome = filters.orgao_nome;
      if (filters?.numero_edital) params.numero_edital = filters.numero_edital;
      if (filters?.portal && filters.portal.length > 0) params.portal = filters.portal.join(',');
      if (filters?.objeto_inclui && filters.objeto_inclui.length > 0) params.objeto_inclui = filters.objeto_inclui.join(',');
      if (filters?.objeto_exclui && filters.objeto_exclui.length > 0) params.objeto_exclui = filters.objeto_exclui.join(',');
      if (filters?.pago !== undefined) params.pago = filters.pago ? 'true' : 'false';
      if (filters?.empresa_ids && filters.empresa_ids.length > 0) params.empresa_ids = filters.empresa_ids.join(',');
      if (filters?.valor_min !== undefined) params.valor_min = filters.valor_min;
      if (filters?.valor_max !== undefined) params.valor_max = filters.valor_max;
      if (filters?.data_de) params.data_de = filters.data_de;
      if (filters?.data_ate) params.data_ate = filters.data_ate;
      if (filters?.categoria) params.categoria = filters.categoria;
      if (filters?.relevancia_min !== undefined) params.relevancia_min = filters.relevancia_min;
      if (filters?.somente_ia) params.somente_ia = 'true';
      if (filters?.sort) params.sort = filters.sort;
      if (filters?.order) params.order = filters.order;

      const response = await api.get<LicitacoesFeedResponse>('/licitacoes/feed', { params });
      return response.data;
    }
  });
}

export function useLicitacoesFontes() {
  return useQuery({
    queryKey: ['licitacoes-fontes'],
    queryFn: async () => {
      const response = await api.get<LicitacoesFontesResponse>('/licitacoes/fontes');
      return response.data.fontes;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useGastoPorNicho(filters?: GastoPorNichoFilters) {
  return useQuery({
    queryKey: ['licitacoes-gasto-por-nicho', filters],
    queryFn: async () => {
      const params: Record<string, string | number> = {
        // Espelha o feed: por padrão o agregado conta apenas licitações
        // ativas, para não divergir da listagem.
        somente_ativas: filters?.somente_ativas !== false ? 'true' : 'false'
      };

      if (filters?.search) params.search = filters.search;
      if (filters?.uf) params.uf = filters.uf;
      if (filters?.ufs_multi && filters.ufs_multi.length > 0) params.ufs_multi = filters.ufs_multi.join(',');
      if (filters?.regioes && filters.regioes.length > 0) params.regioes = filters.regioes.join(',');
      if (filters?.municipio) params.municipio = filters.municipio;
      if (filters?.modalidades && filters.modalidades.length > 0) {
        params.modalidades = filters.modalidades.join(',');
      } else if (filters?.modalidade) {
        params.modalidade = filters.modalidade;
      }
      if (filters?.esferas && filters.esferas.length > 0) params.esferas = filters.esferas.join(',');
      if (filters?.orgao_cnpj) params.orgao_cnpj = filters.orgao_cnpj;
      if (filters?.orgao_nome) params.orgao_nome = filters.orgao_nome;
      if (filters?.numero_edital) params.numero_edital = filters.numero_edital;
      if (filters?.portal && filters.portal.length > 0) params.portal = filters.portal.join(',');
      if (filters?.objeto_inclui && filters.objeto_inclui.length > 0) params.objeto_inclui = filters.objeto_inclui.join(',');
      if (filters?.objeto_exclui && filters.objeto_exclui.length > 0) params.objeto_exclui = filters.objeto_exclui.join(',');
      if (filters?.pago !== undefined) params.pago = filters.pago ? 'true' : 'false';
      if (filters?.valor_min !== undefined) params.valor_min = filters.valor_min;
      if (filters?.valor_max !== undefined) params.valor_max = filters.valor_max;
      if (filters?.data_de) params.data_de = filters.data_de;
      if (filters?.data_ate) params.data_ate = filters.data_ate;
      if (filters?.categoria) params.categoria = filters.categoria;
      if (filters?.relevancia_min !== undefined) params.relevancia_min = filters.relevancia_min;

      const response = await api.get<GastoPorNichoResponse>('/licitacoes/gasto-por-nicho', { params });
      return response.data;
    },
    enabled: !!(filters?.search || filters?.categoria || filters?.objeto_inclui?.length),
  });
}

export interface LicitacaoVencedor {
  id: number;
  cnpj_fornecedor: string;
  nome_fornecedor: string;
  valor_global: number | null;
  data_assinatura: string | null;
  numero_contrato: string | null;
  created_at: string;
}

interface LicitacaoVencedoresResponse {
  success: boolean;
  data: { vencedores: LicitacaoVencedor[] };
}

export function useLicitacaoVencedores(licitacaoId: number, enabled: boolean) {
  return useQuery({
    queryKey: ['licitacao-vencedores', licitacaoId],
    queryFn: async () => {
      const response = await api.get<LicitacaoVencedoresResponse>(`/licitacoes/${licitacaoId}/vencedores`);
      return response.data.vencedores;
    },
    enabled,
  });
}

export function useAtualizarLicitacoes() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await api.post<AtualizarLicitacoesResponse>('/licitacoes/atualizar');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['licitacoes-feed'] });
    }
  });
}
